import { useState } from "react";

const comparisons = [
  {
    name: "UK Social Housing\nStructured Product",
    badge: "Our Product",
    badgeColor: "#C9A84C",
    return: 10.0,
    returnLabel: "10.00% p.a.",
    risk: "Secured",
    riskLevel: 1,
    liquidity: "Monthly Income",
    term: "5 Years",
    security: "1st Legal Charge",
    taxEfficient: true,
    highlight: true,
    icon: "ri-building-2-line",
    description: "Monthly income backed by UK social housing assets with first legal charge security.",
  },
  {
    name: "UK Savings\nAccount",
    badge: "Bank",
    badgeColor: "#64748b",
    return: 4.5,
    returnLabel: "~4.50% p.a.",
    risk: "FSCS Protected",
    riskLevel: 1,
    liquidity: "Instant Access",
    term: "Flexible",
    security: "FSCS up to £85k",
    taxEfficient: false,
    highlight: false,
    icon: "ri-bank-line",
    description: "Standard high-street savings rates, subject to income tax and inflation erosion.",
  },
  {
    name: "UK Government\nGilts (10yr)",
    badge: "Bonds",
    badgeColor: "#64748b",
    return: 4.2,
    returnLabel: "~4.20% p.a.",
    risk: "Low",
    riskLevel: 1,
    liquidity: "Tradeable",
    term: "10 Years",
    security: "Government Backed",
    taxEfficient: false,
    highlight: false,
    icon: "ri-government-line",
    description: "Long-dated government bonds with low yield and full exposure to interest rate risk.",
  },
  {
    name: "FTSE 100\nIndex Fund",
    badge: "Equities",
    badgeColor: "#64748b",
    return: 7.2,
    returnLabel: "~7.20% p.a.*",
    risk: "Market Risk",
    riskLevel: 3,
    liquidity: "Daily",
    term: "Long-term",
    security: "No Capital Protection",
    taxEfficient: false,
    highlight: false,
    icon: "ri-line-chart-line",
    description: "Historical average returns with high volatility and no capital protection.",
  },
  {
    name: "UK Buy-to-Let\nProperty",
    badge: "Property",
    badgeColor: "#64748b",
    return: 5.5,
    returnLabel: "~5.50% p.a.",
    risk: "Illiquid",
    riskLevel: 2,
    liquidity: "Illiquid",
    term: "Open-ended",
    security: "Physical Asset",
    taxEfficient: false,
    highlight: false,
    icon: "ri-home-4-line",
    description: "Rental yield net of costs, with high entry barriers, management burden and stamp duty.",
  },
];

const advantages = [
  {
    icon: "ri-percent-line",
    title: "10% Target Return",
    desc: "More than double the average UK savings rate, with monthly income distributions.",
  },
  {
    icon: "ri-shield-check-line",
    title: "First Legal Charge",
    desc: "Your investment is secured against UK social housing assets — not just a promise.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Monthly Income",
    desc: "Predictable, regular distributions — unlike equities or property rental income.",
  },
  {
    icon: "ri-home-heart-line",
    title: "Social Impact",
    desc: "Your capital funds assisted living and social housing — generating returns with purpose.",
  },
  {
    icon: "ri-time-line",
    title: "Fixed 5-Year Term",
    desc: "Clear investment horizon with defined exit — no open-ended uncertainty.",
  },
  {
    icon: "ri-user-star-line",
    title: "Institutional Grade",
    desc: "Structured product with independent trustee oversight and professional management.",
  },
];

const MAX_RETURN = 12;

export default function WhyInvestSection() {
  const [activeTab, setActiveTab] = useState<"chart" | "table">("chart");

  return (
    <section className="py-24 relative overflow-hidden bg-[#F9F7F3]">
      {/* Subtle background texture */}
      <div
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 50%, rgba(201,168,76,0.06) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(7,26,46,0.04) 0%, transparent 50%)",
        }}
      />

      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <span
            className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.22em] px-4 py-1.5 rounded-full mb-5 border"
            style={{ color: "#C9A84C", borderColor: "rgba(201,168,76,0.35)", background: "rgba(201,168,76,0.08)" }}
          >
            <i className="ri-bar-chart-2-line text-xs leading-none" />
            Why Invest With Us
          </span>
          <h2
            className="text-3xl md:text-5xl font-bold mb-5 leading-tight"
            style={{ fontFamily: "'Playfair Display', serif", color: "#071A2E" }}
          >
            How We Compare to
            <br />
            <span style={{ color: "#C9A84C" }}>Traditional Investments</span>
          </h2>
          <p className="text-slate-500 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            In a low-yield environment, our Social Housing Structured Product targets{" "}
            <strong className="text-[#071A2E]">10% per annum</strong> — significantly outperforming
            savings accounts, gilts, and buy-to-let, with monthly income and first charge security.
          </p>
        </div>

        {/* Tab Toggle */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex items-center bg-white border border-slate-200 rounded-full p-1">
            {(["chart", "table"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab
                    ? "text-[#071A2E]"
                    : "text-slate-400 hover:text-slate-600"
                }`}
                style={
                  activeTab === tab
                    ? { background: "linear-gradient(135deg, #C9A84C, #E2C27A)", color: "#071A2E" }
                    : {}
                }
              >
                <i className={`${tab === "chart" ? "ri-bar-chart-2-line" : "ri-table-2"} mr-1.5 leading-none`} />
                {tab === "chart" ? "Visual Chart" : "Full Comparison"}
              </button>
            ))}
          </div>
        </div>

        {/* Chart View */}
        {activeTab === "chart" && (
          <div className="bg-white rounded-3xl border border-slate-100 p-4 md:p-10 mb-12">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-2 px-2 gap-1">
              <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Target Annual Return (%)</p>
              <p className="text-xs text-slate-400 hidden sm:block">*FTSE 100 historical average. Past performance not indicative of future results.</p>
            </div>

            <div className="space-y-4 mt-6">
              {comparisons.map((item, i) => {
                const pct = (item.return / MAX_RETURN) * 100;
                return (
                  <div key={i} className="flex items-center gap-2 md:gap-4 group">
                    {/* Label */}
                    <div className="w-28 md:w-44 flex-shrink-0 flex items-center gap-1.5 md:gap-2.5">
                      <div
                        className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                        style={{ background: item.highlight ? "rgba(201,168,76,0.15)" : "rgba(100,116,139,0.08)" }}
                      >
                        <i className={`${item.icon} text-xs md:text-sm leading-none`} style={{ color: item.highlight ? "#C9A84C" : "#94a3b8" }} />
                      </div>
                      <span className="text-[10px] md:text-xs font-semibold leading-tight whitespace-pre-line" style={{ color: item.highlight ? "#071A2E" : "#64748b" }}>
                        {item.name}
                      </span>
                    </div>

                    {/* Bar */}
                    <div className="flex-1 relative h-10 flex items-center">
                      <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-700"
                          style={{ width: `${pct}%`, background: item.highlight ? "linear-gradient(90deg, #C9A84C, #E2C27A)" : "linear-gradient(90deg, #cbd5e1, #94a3b8)" }}
                        />
                      </div>
                      <span
                        className="absolute text-[10px] md:text-xs font-bold"
                        style={{ left: `${Math.min(pct, 80)}%`, color: item.highlight ? "#C9A84C" : "#94a3b8" }}
                      >
                        &nbsp;{item.returnLabel}
                      </span>
                    </div>

                    {/* Highlight badge — hidden on very small screens */}
                    {item.highlight && (
                      <span className="hidden sm:flex flex-shrink-0 text-[9px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full border whitespace-nowrap" style={{ color: "#C9A84C", borderColor: "rgba(201,168,76,0.4)", background: "rgba(201,168,76,0.1)" }}>
                        Our Product
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Callout */}
            <div
              className="mt-10 rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 border"
              style={{ background: "rgba(201,168,76,0.06)", borderColor: "rgba(201,168,76,0.2)" }}
            >
              <div
                className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0"
                style={{ background: "rgba(201,168,76,0.15)" }}
              >
                <i className="ri-lightbulb-flash-line text-lg leading-none" style={{ color: "#C9A84C" }} />
              </div>
              <div>
                <p className="text-sm font-bold text-[#071A2E] mb-0.5">
                  10% vs 4.5% — that&apos;s a{" "}
                  <span style={{ color: "#C9A84C" }}>£2,750 difference per year</span> on a £50,000 investment.
                </p>
                <p className="text-xs text-slate-500">
                  Over a 5-year term, the compounding effect of a higher yield makes a substantial difference to your total return.
                  Capital is at risk. Returns are targets only.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Table View */}
        {activeTab === "table" && (
          <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden mb-12">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left px-6 py-4 text-xs font-semibold text-slate-400 uppercase tracking-widest w-40">
                      Metric
                    </th>
                    {comparisons.map((c) => (
                      <th
                        key={c.name}
                        className="px-4 py-4 text-center"
                        style={c.highlight ? { background: "rgba(201,168,76,0.05)" } : {}}
                      >
                        {c.highlight && (
                          <span
                            className="inline-block text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full mb-1.5 border"
                            style={{ color: "#C9A84C", borderColor: "rgba(201,168,76,0.4)", background: "rgba(201,168,76,0.1)" }}
                          >
                            {c.badge}
                          </span>
                        )}
                        <p
                          className="text-xs font-bold leading-snug whitespace-pre-line"
                          style={{ color: c.highlight ? "#071A2E" : "#64748b", fontFamily: c.highlight ? "'Playfair Display', serif" : "inherit" }}
                        >
                          {c.name}
                        </p>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: "Target Return", key: "returnLabel", isReturn: true },
                    { label: "Income Frequency", key: "liquidity" },
                    { label: "Investment Term", key: "term" },
                    { label: "Security", key: "security" },
                    { label: "Risk Profile", key: "risk" },
                  ].map((row, ri) => (
                    <tr key={row.key} className={ri % 2 === 0 ? "bg-slate-50/50" : "bg-white"}>
                      <td className="px-6 py-3.5 text-xs text-slate-500 font-medium whitespace-nowrap border-r border-slate-100">
                        {row.label}
                      </td>
                      {comparisons.map((c) => (
                        <td
                          key={c.name}
                          className="px-4 py-3.5 text-center"
                          style={c.highlight ? { background: "rgba(201,168,76,0.03)" } : {}}
                        >
                          {row.isReturn ? (
                            <span
                              className="font-bold text-sm"
                              style={{
                                color: c.highlight ? "#C9A84C" : "#94a3b8",
                                fontFamily: c.highlight ? "'Playfair Display', serif" : "inherit",
                              }}
                            >
                              {c[row.key as keyof typeof c] as string}
                            </span>
                          ) : (
                            <span
                              className="text-xs font-medium"
                              style={{ color: c.highlight ? "#071A2E" : "#94a3b8" }}
                            >
                              {c[row.key as keyof typeof c] as string}
                            </span>
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50">
              <p className="text-xs text-slate-400 text-center">
                Returns are targets only and not guaranteed. Capital is at risk. Comparisons are illustrative and based on publicly available market data as of 2026.
              </p>
            </div>
          </div>
        )}

        {/* Advantages Grid */}
        <div className="mb-12">
          <h3
            className="text-center text-xl md:text-2xl font-bold mb-8"
            style={{ fontFamily: "'Playfair Display', serif", color: "#071A2E" }}
          >
            Six Reasons Investors Choose Us
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {advantages.map((adv, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-slate-100 p-5 md:p-6 flex gap-4 hover:border-[#C9A84C]/30 transition-all duration-300 group"
              >
                <div
                  className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform duration-300"
                  style={{ background: "rgba(201,168,76,0.12)" }}
                >
                  <i className={`${adv.icon} text-base leading-none`} style={{ color: "#C9A84C" }} />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-[#071A2E] mb-1">{adv.title}</h4>
                  <p className="text-xs text-slate-500 leading-relaxed">{adv.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="text-center">
          <p className="text-slate-400 text-xs mt-4">
            Capital at risk. Past performance is not indicative of future results. For qualified investors only.
          </p>
        </div>
      </div>
    </section>
  );
}
